import * as pc from 'playcanvas'

const RING_NAME = 'surface-ring-marker'
const RING_LAYER_NAME = 'SurfaceRingOverlay'
const RING_SEGMENTS = 96
const RING_THICKNESS_RATIO = 0.22
const RING_DEPTH_RATIO = 0.07
const RING_PIXEL_RADIUS = 32
const RING_DRAW_ORDER = 0x7fffffff
const RING_AUTO_HIDE_MS = 1000
const RING_FADE_OUT_MS = 220
const RING_OPACITY = 0.96
const RING_EMISSIVE_INTENSITY = 0.7
const PICK_SAMPLE_OFFSETS = [8, 14, 22, 32]
const PICK_SAMPLE_DIRECTIONS = [
  { dx: 1, dy: 0 },
  { dx: 1, dy: 1 },
  { dx: 0, dy: 1 },
  { dx: -1, dy: 1 },
  { dx: -1, dy: 0 },
  { dx: -1, dy: -1 },
  { dx: 0, dy: -1 },
  { dx: 1, dy: -1 }
]
const NORMAL_PICKER_MAX_SIZE = 1024
const BASE_NORMAL = new pc.Vec3(0, 0, 1)
const EPSILON = 1e-6

function cloneVec3(value) {
  if (!value || ![value.x, value.y, value.z].every(Number.isFinite)) return null
  return value.clone ? value.clone() : new pc.Vec3(value.x, value.y, value.z)
}

function normalizeVec3(value) {
  const vector = cloneVec3(value)
  if (!vector) return null
  const length = vector.length()
  return Number.isFinite(length) && length >= EPSILON ? vector.mulScalar(1 / length) : null
}

function getCameraFacingNormal(cameraEntity, point) {
  const cameraPosition = cameraEntity?.getPosition?.()
  const hitPoint = cloneVec3(point)
  if (!cameraPosition || !hitPoint) return new pc.Vec3(0, 1, 0)
  return normalizeVec3(cameraPosition.clone().sub(hitPoint)) || new pc.Vec3(0, 1, 0)
}

function orientNormalToCamera(normal, cameraEntity, point) {
  const oriented = normalizeVec3(normal)
  if (!oriented) return getCameraFacingNormal(cameraEntity, point)
  if (oriented.dot(getCameraFacingNormal(cameraEntity, point)) < 0) oriented.mulScalar(-1)
  return oriented
}

async function pickSamples(picker, samplePoints, width, height) {
  if (!picker?.pick || !Number.isFinite(width) || !Number.isFinite(height)) return []
  const samples = samplePoints.map(({ x, y }) => ({
    x: Math.max(0, Math.min(width - 1, Math.round(x))),
    y: Math.max(0, Math.min(height - 1, Math.round(y)))
  }))
  const maxPickerSize = Math.min(NORMAL_PICKER_MAX_SIZE, Math.max(width, height))
  if (typeof picker.pickMany === 'function') {
    return picker.pickMany(samples, width, height, null, maxPickerSize)
  }
  const results = []
  for (const sample of samples) {
    results.push(await picker.pick(sample.x, sample.y, width, height, null, maxPickerSize))
  }
  return results
}

function buildNormalCandidate(center, samples) {
  const vectors = samples
    .map(sample => cloneVec3(sample)?.sub(center))
    .filter(vector => vector && vector.length() > EPSILON)
  if (vectors.length < 2) return null

  const normal = new pc.Vec3()
  if (vectors.length === 2) {
    normal.cross(vectors[0], vectors[1])
  } else {
    for (let index = 0; index < vectors.length; index += 1) {
      normal.add(new pc.Vec3().cross(vectors[index], vectors[(index + 1) % vectors.length]))
    }
  }
  const weight = normal.length()
  const normalized = normalizeVec3(normal)
  return normalized ? { normal: normalized, weight: Math.max(weight, EPSILON) } : null
}

function averageNormalCandidates(candidates, cameraEntity, center) {
  if (!candidates.length) return null
  const averaged = candidates.reduce((sum, candidate) => {
    return sum.add(orientNormalToCamera(candidate.normal, cameraEntity, center).mulScalar(candidate.weight))
  }, new pc.Vec3())
  return orientNormalToCamera(averaged, cameraEntity, center)
}

function estimateWorldUnitsPerPixel(cameraEntity, point, viewportHeight) {
  const camera = cameraEntity?.camera
  const cameraPosition = cameraEntity?.getPosition?.()
  if (!camera || !cameraPosition || !point || !viewportHeight) return null
  if (camera.projection === pc.PROJECTION_ORTHOGRAPHIC) {
    return (camera.orthoHeight || 1) / viewportHeight
  }
  const distance = Math.max(cameraPosition.distance(point), EPSILON)
  return Math.tan((camera.fov || 45) * pc.math.DEG_TO_RAD * 0.5) * distance * 2 / viewportHeight
}

function filterSurfaceSamples(samples, center, cameraEntity, radius, offset, height) {
  const cameraFacing = getCameraFacingNormal(cameraEntity, center)
  const worldUnitsPerPixel = estimateWorldUnitsPerPixel(cameraEntity, center, height)
  const screenSpan = worldUnitsPerPixel ? worldUnitsPerPixel * offset : 0
  const ringRadius = Number.isFinite(radius) && radius > EPSILON ? radius : 0
  const maxDistance = Math.max(ringRadius * 4, screenSpan * 16, 0.01)
  const maxDepthDelta = Math.max(ringRadius * 2.2, screenSpan * 10, 0.006)
  return samples.filter(sample => {
    const point = cloneVec3(sample)
    if (!point) return false
    const delta = point.sub(center)
    const distance = delta.length()
    return Number.isFinite(distance) && distance >= EPSILON && distance <= maxDistance &&
      Math.abs(delta.dot(cameraFacing)) <= maxDepthDelta
  })
}

async function estimateSurfaceNormal({ picker, hitPoint, screenX, screenY, width, height, cameraEntity, radius }) {
  const center = cloneVec3(hitPoint)
  if (!center) return getCameraFacingNormal(cameraEntity, hitPoint)

  const requests = PICK_SAMPLE_OFFSETS.flatMap(offset => PICK_SAMPLE_DIRECTIONS.map(({ dx, dy }) => ({
    x: screenX + dx * offset,
    y: screenY + dy * offset,
    offset
  })))
  const picked = await pickSamples(picker, requests, width, height)
  const byOffset = new Map()
  requests.forEach((sample, index) => {
    if (!picked[index]) return
    const values = byOffset.get(sample.offset) || []
    values.push(picked[index])
    byOffset.set(sample.offset, values)
  })

  const filteredCandidates = []
  const fallbackCandidates = []
  for (const offset of PICK_SAMPLE_OFFSETS) {
    const rawSamples = byOffset.get(offset) || []
    const filtered = filterSurfaceSamples(rawSamples, center, cameraEntity, radius, offset, height)
    const filteredCandidate = buildNormalCandidate(center, filtered)
    const fallbackCandidate = buildNormalCandidate(center, rawSamples)
    if (filteredCandidate) filteredCandidates.push(filteredCandidate)
    if (fallbackCandidate) fallbackCandidates.push(fallbackCandidate)
  }
  return averageNormalCandidates(filteredCandidates, cameraEntity, center) ||
    averageNormalCandidates(fallbackCandidates, cameraEntity, center) ||
    getCameraFacingNormal(cameraEntity, center)
}

function resolveRingRadius({ modelEntity, cameraEntity, hitPoint, height }) {
  const worldUnitsPerPixel = estimateWorldUnitsPerPixel(cameraEntity, hitPoint, height)
  if (Number.isFinite(worldUnitsPerPixel) && worldUnitsPerPixel > EPSILON) {
    return Math.max(worldUnitsPerPixel * RING_PIXEL_RADIUS, EPSILON)
  }
  const halfExtents = modelEntity?.gsplat?.instance?.meshInstance?.aabb?.halfExtents
  const modelRadius = halfExtents
    ? Math.max(Number(halfExtents.x) || 0, Number(halfExtents.y) || 0, Number(halfExtents.z) || 0)
    : 0
  return Math.max(modelRadius * 0.055 || 0.04, 0.01)
}

function resolveRingDepth(radius) {
  return Math.max(radius * RING_DEPTH_RATIO, 0.001)
}

function getOrCreateRingLayer(app) {
  let layer = app?.scene?.layers?.getLayerByName?.(RING_LAYER_NAME)
  if (!layer) {
    layer = new pc.Layer({
      name: RING_LAYER_NAME,
      clearDepthBuffer: true,
      opaqueSortMode: pc.SORTMODE_MANUAL,
      transparentSortMode: pc.SORTMODE_MANUAL
    })
    app.scene.layers.push(layer)
  }
  return layer
}

function ensureCameraRendersLayer(cameraEntity, layer) {
  const camera = cameraEntity?.camera
  if (!camera || !layer) return
  const layers = Array.isArray(camera.layers) ? camera.layers : []
  if (!layers.includes(layer.id)) camera.layers = [...layers, layer.id]
}

function createRingMesh(app, radius) {
  const outerRadius = Math.max(radius, EPSILON)
  const innerRadius = Math.max(outerRadius * (1 - RING_THICKNESS_RATIO), EPSILON)
  const halfDepth = resolveRingDepth(outerRadius) * 0.5
  const positions = []
  const normals = []
  const indices = []

  for (let index = 0; index < RING_SEGMENTS; index += 1) {
    const angle = index / RING_SEGMENTS * Math.PI * 2
    const cos = Math.cos(angle)
    const sin = Math.sin(angle)
    positions.push(
      cos * outerRadius, sin * outerRadius, halfDepth,
      cos * innerRadius, sin * innerRadius, halfDepth,
      cos * outerRadius, sin * outerRadius, -halfDepth,
      cos * innerRadius, sin * innerRadius, -halfDepth
    )
    normals.push(0, 0, 1, 0, 0, 1, 0, 0, -1, 0, 0, -1)
  }

  for (let index = 0; index < RING_SEGMENTS; index += 1) {
    const next = (index + 1) % RING_SEGMENTS
    const outerFront = index * 4
    const innerFront = outerFront + 1
    const outerBack = outerFront + 2
    const innerBack = outerFront + 3
    const nextOuterFront = next * 4
    const nextInnerFront = nextOuterFront + 1
    const nextOuterBack = nextOuterFront + 2
    const nextInnerBack = nextOuterFront + 3
    indices.push(
      outerFront, nextOuterFront, innerFront,
      nextOuterFront, nextInnerFront, innerFront,
      outerBack, innerBack, nextOuterBack,
      nextOuterBack, innerBack, nextInnerBack,
      outerFront, outerBack, nextOuterFront,
      nextOuterFront, outerBack, nextOuterBack,
      innerFront, nextInnerFront, innerBack,
      nextInnerFront, nextInnerBack, innerBack
    )
  }

  const mesh = new pc.Mesh(app.graphicsDevice)
  mesh.setPositions(positions)
  mesh.setNormals(normals)
  mesh.setIndices(indices)
  mesh.update(pc.PRIMITIVE_TRIANGLES)
  return mesh
}

function createRingMaterial() {
  const material = new pc.StandardMaterial()
  material.name = 'surface-ring-marker-material'
  material.diffuse = new pc.Color(1, 1, 1)
  material.emissive = new pc.Color(1, 1, 1)
  material.emissiveIntensity = RING_EMISSIVE_INTENSITY
  material.opacity = RING_OPACITY
  material.blendType = pc.BLEND_NORMAL
  material.useLighting = false
  material.depthTest = false
  material.depthWrite = false
  material.cull = pc.CULLFACE_NONE
  material.update()
  return material
}

function animateSurfaceRingFadeOut(marker, durationMs = RING_FADE_OUT_MS) {
  if (!marker || marker.destroyed || marker._surfaceRingFading) return
  const material = marker._surfaceRingMaterial
  if (!material) {
    clearSurfaceRingMarker(marker)
    return
  }
  marker._surfaceRingFading = true
  const startedAt = performance.now()
  const startOpacity = Number(material.opacity) || RING_OPACITY
  const startEmissive = Number(material.emissiveIntensity) || RING_EMISSIVE_INTENSITY
  const step = timestamp => {
    if (!marker || marker.destroyed) return
    const progress = Math.min(1, Math.max(0, timestamp - startedAt) / Math.max(1, durationMs))
    material.opacity = startOpacity * (1 - progress)
    material.emissiveIntensity = startEmissive * (1 - progress)
    material.update()
    if (progress >= 1) {
      clearSurfaceRingMarker(marker)
    } else {
      marker._surfaceRingFadeFrame = requestAnimationFrame(step)
    }
  }
  marker._surfaceRingFadeFrame = requestAnimationFrame(step)
}

function scheduleSurfaceRingAutoHide(marker, delayMs, fadeMs) {
  if (!marker || !Number.isFinite(delayMs) || delayMs <= 0) return
  marker._surfaceRingHideTimer = window.setTimeout(() => {
    marker._surfaceRingHideTimer = 0
    animateSurfaceRingFadeOut(marker, fadeMs)
  }, delayMs)
}

export function clearSurfaceRingMarker(marker) {
  if (!marker) return null
  if (marker._surfaceRingHideTimer) window.clearTimeout(marker._surfaceRingHideTimer)
  if (marker._surfaceRingFadeFrame) cancelAnimationFrame(marker._surfaceRingFadeFrame)
  marker._surfaceRingHideTimer = 0
  marker._surfaceRingFadeFrame = 0
  marker._surfaceRingFading = false
  const mesh = marker._surfaceRingMesh
  const material = marker._surfaceRingMaterial
  if (!marker.destroyed) marker.destroy()
  mesh?.destroy?.()
  material?.destroy?.()
  marker._surfaceRingMesh = null
  marker._surfaceRingMaterial = null
  return null
}

export async function showSurfaceRingMarker({
  app,
  marker,
  picker,
  hitPoint,
  screenX,
  screenY,
  width,
  height,
  cameraEntity,
  modelEntity,
  autoHideMs = RING_AUTO_HIDE_MS,
  fadeOutMs = RING_FADE_OUT_MS
}) {
  if (!app?.graphicsDevice || !app?.root || !hitPoint || !cameraEntity) return marker || null
  const previousMarker = marker && !marker.destroyed ? marker : null
  const wasEnabled = previousMarker?.enabled
  if (previousMarker) previousMarker.enabled = false
  const radius = resolveRingRadius({ modelEntity, cameraEntity, hitPoint, height })
  let normal
  try {
    normal = await estimateSurfaceNormal({
      picker,
      hitPoint,
      screenX,
      screenY,
      width,
      height,
      cameraEntity,
      radius
    })
  } finally {
    if (previousMarker && !previousMarker.destroyed) previousMarker.enabled = wasEnabled
  }
  clearSurfaceRingMarker(previousMarker)

  const orientedNormal = orientNormalToCamera(normal, cameraEntity, hitPoint)
  const offset = Math.max(radius * 0.08, resolveRingDepth(radius) * 1.25, 0.001)
  const position = cloneVec3(hitPoint).add(orientedNormal.clone().mulScalar(offset))
  const layer = getOrCreateRingLayer(app)
  ensureCameraRendersLayer(cameraEntity, layer)
  const mesh = createRingMesh(app, radius)
  const material = createRingMaterial()
  const entity = new pc.Entity(RING_NAME, app)
  const meshInstance = new pc.MeshInstance(mesh, material, entity)
  meshInstance.pick = false
  meshInstance.drawOrder = RING_DRAW_ORDER
  entity._surfaceRingMesh = mesh
  entity._surfaceRingMaterial = material
  entity.addComponent('render', {
    meshInstances: [meshInstance],
    layers: [layer.id],
    castShadows: false,
    receiveShadows: false
  })
  entity.setPosition(position)
  entity.setRotation(new pc.Quat().setFromDirections(BASE_NORMAL, orientedNormal))
  app.root.addChild(entity)
  scheduleSurfaceRingAutoHide(entity, autoHideMs, fadeOutMs)
  return entity
}
